import { api } from '@/Data/api';
import ss from '../Session';
import {
    BookmarkType,
    SocialResponse,
    CreateBookmarkParams,
    GetBookmarkParams,
    DeleteBookmarkParams
} from './types';

/**
 * Bookmark a post to a folder
 * @param params CreateBookmarkParams object with postid and optional folder_name
 * @returns Promise with bookmark data or null if failed
 */
export const bookmarkPost = async (params: CreateBookmarkParams): Promise<BookmarkType | null> => {
    try {
        const res = await api.post<SocialResponse<BookmarkType>>('/social/bookmark', params, {
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? res.data.data ?? null : null;
    } catch (e) {
        console.error('Failed to bookmark post:', e);
        return null;
    }
};

/**
 * List user's bookmarks with optional folder filtering
 * @param params GetBookmarkParams object with optional folder_name, limit, and offset
 * @returns Promise with array of bookmarks
 */
export const listBookmarks = async (params: GetBookmarkParams = {}): Promise<BookmarkType[] | null> => {
    try {
        const res = await api.get<SocialResponse<BookmarkType[]>>('/social/bookmark', {
            params,
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200 ? res.data.data ?? null : null;
    } catch (e) {
        console.error('Failed to list bookmarks:', e);
        return null;
    }
};

/**
 * List all bookmarks for current user
 * @param limit Maximum bookmarks to return
 * @param offset Number of bookmarks to skip
 * @returns Promise with array of all user's bookmarks
 */
export const listAllBookmarks = async (limit?: number, offset?: number): Promise<BookmarkType[] | null> => {
    try {
        const params: GetBookmarkParams = {};
        if (limit !== undefined) params.limit = limit;
        if (offset !== undefined) params.offset = offset;
        
        return await listBookmarks(params);
    } catch (e) {
        console.error('Failed to list all bookmarks:', e);
        return null;
    }
};

/**
 * List bookmarks from a specific folder
 * @param folder_name Folder name to filter by
 * @param limit Maximum bookmarks to return
 * @param offset Number of bookmarks to skip
 * @returns Promise with array of bookmarks from the folder
 */
export const listFolderBookmarks = async (folder_name: string, limit?: number, offset?: number): Promise<BookmarkType[] | null> => {
    try {
        const params: GetBookmarkParams = { folder_name };
        if (limit !== undefined) params.limit = limit;
        if (offset !== undefined) params.offset = offset;
        
        return await listBookmarks(params);
    } catch (e) {
        console.error(`Failed to list bookmarks from folder ${folder_name}:`, e);
        return null;
    }
};

/**
 * List bookmarks from the default folder
 * @param limit Maximum bookmarks to return
 * @param offset Number of bookmarks to skip
 * @returns Promise with array of bookmarks from default folder
 */
export const listDefaultBookmarks = async (limit?: number, offset?: number): Promise<BookmarkType[] | null> => {
    try {
        return await listFolderBookmarks('default', limit, offset);
    } catch (e) {
        console.error('Failed to list default bookmarks:', e);
        return null;
    }
};

/**
 * Remove a bookmark
 * @param params DeleteBookmarkParams object with postid
 * @returns Promise with boolean indicating success
 */
export const removeBookmark = async (params: DeleteBookmarkParams): Promise<boolean> => {
    try {
        const res = await api.delete<SocialResponse<{message: string}>>('/social/bookmark', {
            params,
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });
        
        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to remove bookmark:', e);
        return false;
    }
};